import React, { useState } from "react";
import { ArrowLeft, ArrowRight, KeyRound, MessageCircleMore } from "lucide-react";
import { Link } from "react-router-dom";
import fetchData from "../../api/fetchApi";
import { getLang } from "../../i18n";

export default function ForgotPasswordPage() {
  const isArabic = getLang() === "ar";
  const [step, setStep] = useState("request");
  const [username, setUsername] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const requestReset = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetchData(
        "user/request_password_reset",
        "POST",
        JSON.stringify({ username: username.trim() })
      );
      setResetToken(response.reset_token);
      setStep("reset");
      setMessage(isArabic ? "أرسلنا رمز التحقق إلى رقم واتساب المسجل." : "We sent a code to your registered WhatsApp number.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(isArabic ? "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل." : "Use at least 8 characters.");
      return;
    }
    if (password !== confirmation) {
      setError(isArabic ? "كلمتا المرور غير متطابقتين." : "The passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await fetchData(
        "user/reset_password",
        "POST",
        JSON.stringify({
          reset_token: resetToken,
          otp_code: otpCode.trim(),
          new_password: password,
          new_password_confirmation: confirmation,
        })
      );
      setStep("complete");
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setLoading(false);
    }
  };

  const BackIcon = isArabic ? ArrowRight : ArrowLeft;

  return (
    <div className="auth-shell" dir={isArabic ? "rtl" : "ltr"}>
      <Link className="auth-brand" to="/">
        <span className="auth-brand-mark"><MessageCircleMore size={24} /></span>
        <span>واتسي</span>
      </Link>
      <section className="auth-card auth-card-narrow" aria-labelledby="reset-title">
        <Link to="/auth/login" className="back-link"><BackIcon size={17} />{isArabic ? "العودة للدخول" : "Back to sign in"}</Link>
        <div className="auth-card-copy">
          <span className="eyebrow">{isArabic ? "استعادة آمنة" : "Secure recovery"}</span>
          <h1 id="reset-title">{isArabic ? "إعادة تعيين كلمة المرور" : "Reset your password"}</h1>
          <p>{isArabic ? "سنرسل رمز تحقق عبر واتساب إلى الرقم المسجل بالحساب." : "We’ll send a verification code to the WhatsApp number on your account."}</p>
        </div>

        {step === "request" && (
          <form className="auth-form" onSubmit={requestReset}>
            <label className="field-label" htmlFor="reset-username">{isArabic ? "اسم المستخدم" : "Username"}</label>
            <input id="reset-username" className="field-control" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" required />
            {error && <div className="notice notice-error" role="alert">{error}</div>}
            <button className="primary-button" disabled={loading || !username.trim()}><KeyRound size={18} />{loading ? (isArabic ? "جاري الإرسال…" : "Sending…") : (isArabic ? "إرسال رمز التحقق" : "Send verification code")}</button>
          </form>
        )}

        {step === "reset" && (
          <form className="auth-form" onSubmit={resetPassword}>
            {message && <div className="notice notice-success" role="status">{message}</div>}
            <label className="field-label" htmlFor="otp-code">{isArabic ? "رمز التحقق" : "Verification code"}</label>
            <input id="otp-code" className="field-control code-input" inputMode="numeric" value={otpCode} onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required />
            <label className="field-label" htmlFor="new-password">{isArabic ? "كلمة المرور الجديدة" : "New password"}</label>
            <input id="new-password" className="field-control" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required />
            <label className="field-label" htmlFor="confirm-password">{isArabic ? "تأكيد كلمة المرور" : "Confirm password"}</label>
            <input id="confirm-password" className="field-control" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" required />
            {error && <div className="notice notice-error" role="alert">{error}</div>}
            <button className="primary-button" disabled={loading || otpCode.length !== 6}>{loading ? (isArabic ? "جاري الحفظ…" : "Saving…") : (isArabic ? "حفظ كلمة المرور" : "Save password")}</button>
          </form>
        )}

        {step === "complete" && (
          <div className="auth-form">
            <div className="notice notice-success" role="status">{isArabic ? "تم تغيير كلمة المرور. يمكنك تسجيل الدخول الآن." : "Your password has been changed. You can sign in now."}</div>
            <Link to="/auth/login" className="primary-button">{isArabic ? "الذهاب إلى تسجيل الدخول" : "Go to sign in"}</Link>
          </div>
        )}
      </section>
    </div>
  );
}
